import React, { useEffect } from 'react';
import './Modal.scss';
import PropTypes from 'prop-types';

export default function Modal({
	isOpen,
	onClose,
	children,
	title,
	bodyClassName = '',
	overlayKeyboardClosable = false, // optional: allow Enter/Space to close overlay
}) {
	useEffect(() => {
		if (!isOpen) return;

		const handleKeyDown = (e) => {
			if (e.key === 'Escape') {
				onClose();
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		document.body.style.overflow = 'hidden';

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
			document.body.style.overflow = '';
		};
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	const handleOverlayClick = (e) => {
		// Only close if the overlay itself was clicked
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	const handleOverlayKeyDown = (e) => {
		if (overlayKeyboardClosable && (e.key === 'Enter' || e.key === ' ')) {
			e.preventDefault();
			onClose();
		}
	};

	return (
		<div
			className="modal-overlay"
			onClick={handleOverlayClick}
			role={overlayKeyboardClosable ? 'button' : 'presentation'}
			tabIndex={overlayKeyboardClosable ? 0 : -1}
			onKeyDown={handleOverlayKeyDown}
			aria-label={overlayKeyboardClosable ? 'Close modal' : undefined}
		>
			<div
				className="modal-container"
				role="dialog"
				aria-modal="true"
				aria-labelledby="modal-title"
			>
				<div className="modal-header">
					<span
						id="modal-title"
						className="modal-title"
					>
						{title}
					</span>
					<button
						type="button"
						onClick={onClose}
						className="modal-close"
						aria-label="Close modal"
					>
						Close
					</button>
				</div>

				<div className={`modal-body ${bodyClassName}`}>{children}</div>
			</div>
		</div>
	);
}

Modal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	children: PropTypes.node,
	title: PropTypes.string,
	bodyClassName: PropTypes.string,
	overlayKeyboardClosable: PropTypes.bool, // new optional prop
};
